import React, { useEffect, useState } from 'react';
import {
  Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Link, Typography
} from '@mui/material';
import { useAuth } from '../../Helpers/Contexts/AuthContext';
import { TERMS_VERSION } from '../../Pages/TermsPage';
import { supabase } from '../../supabaseClient';

/**
 * Bloqueia o sistema até o usuário aceitar a versão atual dos Termos.
 * O aceite fica gravado em user_profiles (terms_accepted_at / terms_version) e no audit_log.
 */
export default function TermsGate({ children }) {
  const { user, signOut } = useAuth();
  const [status, setStatus] = useState('loading'); // loading | pending | accepted
  const [checked, setChecked] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!user?.id) return undefined;
    let alive = true;
    setStatus('loading');
    supabase.from('user_profiles').select('terms_version').eq('id', user.id).maybeSingle()
      .then(({ data, error: err }) => {
        if (!alive) return;
        // Erro de leitura não tranca ninguém fora do sistema: o aceite é pedido no próximo acesso
        if (err) { setStatus('accepted'); return; }
        setStatus(data?.terms_version === TERMS_VERSION ? 'accepted' : 'pending');
      }, () => alive && setStatus('accepted'));
    return () => { alive = false; };
  }, [user?.id]);

  const accept = async () => {
    setSaving(true);
    setError('');
    const { error: err } = await supabase.from('user_profiles').upsert(
      { id: user.id, email: user.email, terms_accepted_at: new Date().toISOString(), terms_version: TERMS_VERSION },
      { onConflict: 'id' }
    );
    if (err) {
      setError('Não foi possível registrar o aceite. Tente novamente.');
      setSaving(false);
      return;
    }
    supabase.rpc('log_event', { p_action: 'TERMS_ACCEPTED', p_summary: `Aceitou os Termos (versão ${TERMS_VERSION})` })
      .then(() => {}, () => {});
    setStatus('accepted');
  };

  if (status === 'accepted') return children;
  if (status === 'loading') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress color="primary" />
      </Box>
    );
  }

  return (
    <Dialog open fullWidth maxWidth="sm" aria-labelledby="terms-gate-title" data-demo-allow>
      <DialogTitle id="terms-gate-title" sx={{ fontWeight: 700 }}>Termos de Uso e Privacidade</DialogTitle>
      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Typography variant="body2">Antes de continuar, leia e aceite os Termos. Em resumo:</Typography>
        <Box component="ul" sx={{ m: 0, pl: 2.5, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          <Typography component="li" variant="body2">O acesso é pessoal e você só vê as lojas às quais está vinculado.</Typography>
          <Typography component="li" variant="body2">É proibido copiar o sistema, fazer engenharia reversa, coletar dados de forma automatizada ou usá-lo para criar um produto concorrente.</Typography>
          <Typography component="li" variant="body2">Registramos seu nome, e-mail e o que você faz no sistema (telas acessadas e alterações) para segurança, auditoria e contato comercial.</Typography>
          <Typography component="li" variant="body2">O histórico do que você fez em uma loja continua no registro dela mesmo se você for desvinculado.</Typography>
        </Box>
        <Link href="/termos" target="_blank" rel="noopener noreferrer" variant="body2" className="touch-target" sx={{ fontWeight: 600 }}>
          Ler os Termos completos (abre em nova aba)
        </Link>
        <FormControlLabel
          control={<Checkbox checked={checked} onChange={(e) => setChecked(e.target.checked)} />}
          label="Li e aceito os Termos de Uso e a Política de Privacidade"
        />
        {error && <Typography variant="body2" color="error" role="alert">{error}</Typography>}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={signOut} sx={{ textTransform: 'none' }}>Sair</Button>
        <Button variant="contained" disabled={!checked || saving} onClick={accept} sx={{ textTransform: 'none', fontWeight: 600 }}>
          {saving ? 'Salvando...' : 'Aceitar e continuar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
